// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';

import { MIN_PASSWORD_LENGTH } from '@/features/profile/passwordValidation';
import { BffWebSocketClient, UserPasswordChangedEvent, getSharedBffWebSocket } from '@/services/bffWebSocket';
import { useI18n } from '@/i18n';

const PASSWORD_REQUEST_TIMEOUT_MS = 15000;

type Translate = (key: string, vars?: Record<string, string | number>) => string;

type UseChangePasswordParams = {
  apiBaseUrl: string;
  token?: string | null;
  onSuccess?: () => void;
};

export const useChangePassword = ({ apiBaseUrl, token, onSuccess }: UseChangePasswordParams) => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef<string | null>(null);
  const socketRef = useRef<BffWebSocketClient | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSuccessRef = useRef(onSuccess);
  const tokenRef = useRef(token);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const clearPendingTimeout = useCallback(() => {
    if (!pendingTimeoutRef.current) return;
    clearTimeout(pendingTimeoutRef.current);
    pendingTimeoutRef.current = null;
  }, []);

  useEffect(() => {
    const client = getSharedBffWebSocket(apiBaseUrl);
    socketRef.current = client;
    const unsubscribe = client.onMessage((message) => {
      const event = message as UserPasswordChangedEvent;
      if (event?.type !== 'user.password.changed') return;
      if (!requestIdRef.current || event.requestId !== requestIdRef.current) return;
      requestIdRef.current = null;
      clearPendingTimeout();
      setLoading(false);
      if (event.success === false) {
        setError(mapChangeError(t, event.error));
        return;
      }
      setError(null);
      onSuccessRef.current?.();
    });
    const onConn = client.onConnectionChange((state) => {
      if (state === 'disconnected' && requestIdRef.current) {
        console.warn('[profile] BFF disconnected while user.password.change pending', {
          requestId: requestIdRef.current,
        });
        requestIdRef.current = null;
        clearPendingTimeout();
        setLoading(false);
        setError(t('profile.passwordChangeFailed'));
      }
    });
    return () => {
      unsubscribe();
      onConn();
      clearPendingTimeout();
    };
  }, [apiBaseUrl, clearPendingTimeout, t]);

  const changePassword = useCallback(
    (currentPassword: string | null, newPassword: string) => {
      if (newPassword.length < MIN_PASSWORD_LENGTH) {
        setError(t('profile.passwordTooShort', { min: MIN_PASSWORD_LENGTH }));
        return;
      }
      const client = socketRef.current;
      if (!client) {
        setError(t('profile.passwordChangeFailed'));
        return;
      }
      const requestId = generateRequestId();
      requestIdRef.current = requestId;
      setLoading(true);
      setError(null);
      clearPendingTimeout();
      pendingTimeoutRef.current = setTimeout(() => {
        if (!requestIdRef.current) return;
        console.warn('[profile] user.password.change timed out', { requestId: requestIdRef.current });
        requestIdRef.current = null;
        setLoading(false);
        setError(t('profile.passwordChangeTimeout'));
      }, PASSWORD_REQUEST_TIMEOUT_MS);
      client.setAuthToken(tokenRef.current ?? null);
      // 'queued' is not a failure — the frame is flushed when the socket (re)opens and the
      // request is still in flight; the pending timeout armed above is the only thing that gives up.
      client.sendUserPasswordChange({
        newPassword,
        currentPassword: currentPassword ?? undefined,
        requestId,
      });
    },
    [clearPendingTimeout, t]
  );

  const reset = useCallback(() => {
    requestIdRef.current = null;
    clearPendingTimeout();
    setLoading(false);
    setError(null);
  }, [clearPendingTimeout]);

  return { changePassword, loading, error, reset };
};

const mapChangeError = (t: Translate, error?: string) => {
  switch (error) {
    case 'password.change.currentInvalid':
      return t('profile.currentPasswordInvalid');
    case 'password.change.currentRequired':
      return t('profile.currentPasswordRequired');
    case 'password.change.accountLocked':
      return t('profile.passwordAccountLocked');
    case 'password.change.invalid':
      return t('profile.passwordTooShort', { min: MIN_PASSWORD_LENGTH });
    default:
      return t('profile.passwordChangeFailed');
  }
};

const generateRequestId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  return `req-${Date.now()}`;
};
